import { NotImplementedError } from "../../../../errors";
import { parseDateFilter } from "../../../../models/looker/modules/overview";
import { PromiseHandler } from "../../../../handlers";
import Advertiser from "./filters/Advertiser";
import Breakdown from "./Breakdown";
import Campaign from "./filters/Campaign";
import CampaignDeliveryStatus from "./CampaignDeliveryStatus";
import GeoMap from "./GeoMap";
import GeoZcta from "./GeoZcta";
import Geofences from "./Geofences";
import DailyImpressions from "./DailyImpressions";
import PartnerLogo from "./PartnerLogo";
import Creatives from "./Creatives";
import MediaType from "./filters/MediaType";
import PerformanceByProduct from "./PerformanceByProduct";
import Placements from "./Placements";
import Quartiles from "./Quartiles";
import Screenshots from "./Screenshots";
import SpendAnalysis from "./SpendAnalysis";
import { TOP_LINE_CALLS_QUERY, TOP_LINE_METRICS_QUERY } from "./query";
class Overview extends PromiseHandler {
    constructor(sdk) {
        super(sdk, "looker-overview");
        this.filters = {
            advertiser: new Advertiser(sdk),
            campaign: new Campaign(sdk),
            mediaType: new MediaType(sdk)
        };
        this.placements = new Placements(sdk);
        this.quartiles = new Quartiles(sdk);
        this.breakdown = new Breakdown(sdk);
        this.campaignDeliveryStatus = new CampaignDeliveryStatus(sdk);
        this.partnerLogo = new PartnerLogo(sdk);
        this.creatives = new Creatives(sdk);
        this.dailyImpressions = new DailyImpressions(sdk);
        this.performanceByProduct = new PerformanceByProduct(sdk);
        this.geoMap = new GeoMap(sdk);
        this.geoZcta = new GeoZcta(sdk);
        this.geofences = new Geofences(sdk);
        this.screenshots = new Screenshots(sdk);
        this.spendAnalysis = new SpendAnalysis(sdk);
    }
    async findItems(filters) {
        const dateFilter = filters.where?.find((filter) => filter.field === "dates");
        if (!dateFilter) {
            throw new Error("Dates filter is required");
        }
        const lookbackFilter = filters.where?.find((filter) => filter.field === "lookbackDays");
        if (!lookbackFilter) {
            throw new Error("Lookback filter is required");
        }
        const queryBody = {
            ...TOP_LINE_METRICS_QUERY,
            filters: {
                "explore_overview.flt_date_range": parseDateFilter(dateFilter),
                "explore_overview.pmt_lookback_window": String(lookbackFilter.value)
            }
        };
        const mediaTypeFilter = filters.where?.find((filter) => filter.field === "mediaType");
        if (mediaTypeFilter?.value) {
            queryBody.filters["explore_overview.product_group"] = mediaTypeFilter.value;
        }
        // Fail closed: the metrics are scoped by advertiser + campaign (which are
        // themselves gated by the partner's email-scoped filter lists). Both are
        // mandatory — without them the query would aggregate across every partner,
        // so we refuse to run it rather than leak cross-tenant data.
        const advertiserFilter = filters.where?.find((filter) => filter.field === "advertiserIds");
        const advertiserIds = advertiserFilter?.value;
        if (!advertiserIds?.length) {
            throw new Error("Advertiser ids filter is required");
        }
        queryBody.filters["meta_inst.advertiser_id"] = advertiserIds.join(",");
        const campaignFilter = filters.where?.find((filter) => filter.field === "campaignIds");
        const campaignIds = campaignFilter?.value;
        if (!campaignIds?.length) {
            throw new Error("Campaign ids filter is required");
        }
        queryBody.filters["meta_inst.campaign_id"] = campaignIds.join(",");
        // Calls (SEM) is fetched in a separate query that reuses the same filters.
        // Keeping calls.total_calls out of the five-metric query means its joined
        // view can never fan out (multiply) the explore_overview aggregates.
        const callsQueryBody = {
            ...TOP_LINE_CALLS_QUERY,
            filters: queryBody.filters
        };
        const [metricsHash, callsHash] = await Promise.all([
            this.sdk.looker.query.hashQuery(queryBody),
            this.sdk.looker.query.hashQuery(callsQueryBody)
        ]);
        return this.cache.promise(`${metricsHash}:${callsHash}`, () => Promise.all([
            this.sdk.looker.query.run(queryBody),
            // Calls is independent of the five metrics: if its query fails, fall
            // back to zero calls rather than taking down the whole widget.
            this.sdk.looker.query
                .run(callsQueryBody)
                .then((res) => res.data)
                .catch((e) => {
                console.error(e);
                return [];
            })
        ])
            .then(([metricsRes, callsData]) => [
            {
                data: this.formatResponse(metricsRes.data, callsData),
                queryMeta: metricsRes.queryMeta
            }
        ])
            .catch((e) => {
            console.error(e);
            throw new Error("Failed to fetch overview dashboard top line metrics");
        }));
    }
    formatResponse(metricsData, callsData) {
        const result = metricsData[0];
        const calls = callsData[0];
        return [
            {
                totalImpressions: result?.["explore_overview.total_impressions"] || 0,
                totalVideoCompletes: result?.["explore_overview.total_video_completes"] || 0,
                totalClicks: result?.["explore_overview.total_clicks"] || 0,
                siteVisits: result?.["explore_overview.site_visits"] || 0,
                viewThroughs: result?.["explore_overview.view_through_conversions"] || 0,
                totalCalls: calls?.["calls.total_calls"] || 0
            }
        ];
    }
    async saveItem() {
        throw new NotImplementedError("saveItem");
    }
    async deleteItem() {
        throw new NotImplementedError("deleteItem");
    }
}
export default Overview;
//# sourceMappingURL=index.js.map